import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

export interface AuditLogEntry {
  id: string;
  occurred_at: string;
  actor_id: string;
  actor_email: string;
  action: string;
  entity_type: string;
  entity_id: string;
  entity_name: string;
  changes_json: any;
  ip: string | null;
  client: string;
  risk_level?: string;
  organization_id?: string;
  organization_name?: string;
}

export interface AuditLogDetail extends AuditLogEntry {
  organization_name?: string;
  diff: Array<{
    field: string;
    old: string;
    new: string;
  }>;
}

export interface AuditLogFilters {
  search?: string;
  action?: string;
  entity_type?: string;
  actor?: string;
  from?: string;
  to?: string;
  page?: number;
  page_size?: number;
}

export const useAuditLogs = (filters: AuditLogFilters) => {
  const queryClient = useQueryClient();

  // Set up realtime subscription for new audit logs
  useEffect(() => {
    const channel = supabase
      .channel('audit-logs-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'security_audit_log'
        },
        () => {
          // Invalidate and refetch audit logs when new entries arrive
          queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return useQuery({
    queryKey: ["audit-logs", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set("q", filters.search);
      if (filters.action) params.set("action", filters.action);
      if (filters.entity_type) params.set("entity_type", filters.entity_type);
      if (filters.actor) params.set("actor", filters.actor);
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      params.set("page", String(filters.page || 1));
      params.set("page_size", String(filters.page_size || 50));

      const queryString = params.toString();
      const { data, error } = await supabase.functions.invoke(
        `admin-audit-logs?${queryString}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (error) throw error;
      return data as {
        items: AuditLogEntry[];
        page: number;
        page_size: number;
        total: number;
      };
    },
    enabled: !!filters,
  });
};

export const useAuditLogDetail = (id: string | null) => {
  return useQuery({
    queryKey: ["audit-log-detail", id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase.functions.invoke(
        `admin-audit-logs/${id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (error) throw error;
      return data as AuditLogDetail;
    },
    enabled: !!id,
  });
};

export const useExportAuditLogs = () => {
  return useMutation({
    mutationFn: async (filters: AuditLogFilters) => {
      const params = new URLSearchParams();
      if (filters.search) params.set("q", filters.search);
      if (filters.action) params.set("action", filters.action);
      if (filters.entity_type) params.set("entity_type", filters.entity_type);
      if (filters.actor) params.set("actor", filters.actor);
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);

      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      if (!token) throw new Error("Not authenticated");

      const supabaseUrl = "https://crpxcsrljmdcrgnedfxb.supabase.co";
      const response = await fetch(
        `${supabaseUrl}/functions/v1/admin-audit-logs/export.csv?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });
};
